import { Button, Card, Flex } from '@aws-amplify/ui-react';
import { useCallback, useContext, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import LoadingBars from 'src/custom-components/LoadingBars';
import { TokenPagination } from '../components/TokenPagination';
import CustomList from '../custom-components/CustomList';
import { GlobalContext } from '../global-context';
import { useTokenPager } from '../services/edulead-v4';
import { eduleadV4 } from '../services/edulead-v4-client';
import { Entity } from '../utils/CustomTypes';
import General from '../utils/GeneralUtils';
import { legacyPeopleResponse } from '../utils/legacyPerson';
import { LocalStorageKeys, LocalStorageService } from '../utils/LocalStorageService';
import { handleNewExport } from '../utils/newExportFeature';

const PAGE_SIZE = 50;

export function InstitutionContacts(props: any) {

  const globalContext = useContext(GlobalContext);
  const navigate = useNavigate();

  const { id } = useParams()
  const [institutionId] = useState(id);

  const [isContactsTableExpanded, setIsContactsTableExpanded] = useState(LocalStorageService.get(LocalStorageKeys.IS_CONTACTS_LIST_ON_PARENT_PAGE_EXPANDED) === 'true');
  const [selectedRows, setSelectedRows] = useState<{[key: string]: any}>({});

  const pager = useTokenPager(
    (pageToken) => eduleadV4.people.search({
      conditions: JSON.stringify([{ field: 'institutionId', operator: 'equals', value: institutionId }]),
      source: 'INSTITUTION_DETAIL',
      order_by: 'priority desc',
      page_size: PAGE_SIZE,
      page_token: pageToken,
    }),
    [institutionId],
    'Something went wrong loading contacts'
  );
  const contactsSearchResponse = pager.items && pager.response && legacyPeopleResponse(pager.items, pager.response);
  const isLoadingContacts = pager.loading && !pager.items;
  const isPaginationLoading = pager.loading && !!pager.items;

  // Create search params for export
  const exportSearchParams = useMemo(() => ({
    pageSize: 200,
    page: 1,
    entity: 'contacts',
    q: General.getQ({ rules: [{ field: 'institutionId', value: institutionId, operator: 'equals' }] }),
    sortBy: 'priority',
    sortDir: 'desc',
  }), [institutionId, contactsSearchResponse?.data?.total]);

  // Callback for export
  const handleNewExportClick = useCallback(async (config: { type: 'selected' | 'all'; ids?: string[] }) => {
    const estimatedCount = config.type === 'selected' && config.ids?.length
      ? config.ids.length
      : contactsSearchResponse?.data?.total || 0;

    const exportId = await handleNewExport({
      ...config,
      entity: 'CONTACT',
      searchParams: exportSearchParams,
      estimatedCount,
    });
    navigate(`/exports/${exportId}/target`);
  }, [exportSearchParams, navigate, contactsSearchResponse]);

  return (
    <>
      <GlobalContext.Consumer>
        {(globalContextProps) =>
          <div className={`content ${isContactsTableExpanded ? 'content-1600' : 'content-1000'}`}>
            <Flex justifyContent='flex-end' style={{ height: 40, marginTop: -50 }}>
              <div>
                <Button
                  onClick={() => {
                    navigate(`/directory/people?q=${General.getQ({
                      rules: [{
                        field: 'institutionId',
                        value: institutionId,
                        operator: 'equals'
                      }]
                    })}`);
                  }}
                  className='tiny nohover'
                  size='small'
                >
                  Filter contacts
                </Button>
              </div>
            </Flex>
            {
              isLoadingContacts
                ? <LoadingBars numberOfRows={6} />
                : contactsSearchResponse?.data?.data?.length
                  ? <CustomList
                      searchResponse={contactsSearchResponse}
                      isLoading={isLoadingContacts}
                      isPaginationLoading={isPaginationLoading}
                      isExporting={false}
                      onSort={() => {}}
                      showRowActionButtons={false}
                      showMainInstitution={false}
                      showExportButton={true}
                      showSubheader={false}
                      isAllSortingDisabled={true}
                      onExport={() => {}}
                      onNewExport={handleNewExportClick}
                      isExpanded={isContactsTableExpanded}
                      onExpandToggle={() => {
                        const newValue = !isContactsTableExpanded
                        setIsContactsTableExpanded(newValue)
                        LocalStorageService.set(LocalStorageKeys.IS_CONTACTS_LIST_ON_PARENT_PAGE_EXPANDED, newValue)
                      }}
                      currentSort={{
                        sortBy: 'priority',
                        sortDir: 'desc'
                      }}
                      tableFields={globalContext.environment.entities.contact.tableFields}
                      showExpandButton={true}
                      entity={Entity.CONTACT}
                      onEvent={() => {}}
                      selectedRows={selectedRows}
                      onSelectionChange={setSelectedRows}
                    />
                  : <Card variation='outlined'>
                      <Flex direction='column' justifyContent='center' alignItems='center' columnGap={10}>
                        <div className='desc-2'>
                          No contacts found. <Link to={`/foia`}>Request a public information request (FOIA)</Link> to get access to this institution's contacts.
                        </div>
                      </Flex>
                    </Card>
            }
            {
              contactsSearchResponse && (
                <TokenPagination
                  page={pager.page}
                  pageSize={PAGE_SIZE}
                  shown={contactsSearchResponse.data.data.length}
                  total={contactsSearchResponse.data.total}
                  hasNext={pager.hasNext}
                  isLoading={pager.loading}
                  onPrevious={pager.prev}
                  onNext={pager.next}
                />
              )
            }
          </div>
        }
      </GlobalContext.Consumer>
      
    </>
  );
}